import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  getRelationship,
  updateRelationship,
} from "../redux/actions/relationshipActions";
import {
  Container,
  Box,
  FormControl,
  Stack,
  FormLabel,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Tabs,
  Tab,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
  IconButton,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { textFieldStyles } from "../styles/textFieldStyles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { verticalTabStyles } from "../styles/verticalTabStyles";
import CancelButton from "../components/CancelButton";
import SubmitButton from "../components/SubmitButton";

import { useLocation } from "react-router-dom";
const getBreadcrumbs = (t) => [
  { label: t('pages:dashboard.title'), href: "/" },
  { label: t('common:relationships'), href: "/relationships" },
];
const ViewEditRelationshipPage = () => {
  const { t } = useTranslation(['common', 'pages', 'forms', 'buttons']);
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  // List of marital status options
  const maritalStatusOptions = [
    { value: "Single", label: t('forms:options.marital_status.single') },
    { value: "Married", label: t('forms:options.marital_status.married') },
    { value: "Divorced", label: t('forms:options.marital_status.divorced') },
    { value: "Widowed", label: t('forms:options.marital_status.widowed') }
  ];
  // List of sex options
  const sexOptions = [
    { value: "Male", label: t('forms:options.sex.male') },
    { value: "Female", label: t('forms:options.sex.female') },
    { value: "Unknown", label: t('forms:options.sex.unknown') }
  ];
  // List of European countries with translations
  const europeanCountries = [
    { value: "Netherlands", label: t('common:countries.Netherlands') },
    { value: "Belgium", label: t('common:countries.Belgium') },
    { value: "Germany", label: t('common:countries.Germany') },
    { value: "France", label: t('common:countries.France') },
    { value: "Italy", label: t('common:countries.Italy') },
    { value: "Spain", label: t('common:countries.Spain') },
    { value: "Portugal", label: t('common:countries.Portugal') },
    { value: "Sweden", label: t('common:countries.Sweden') },
    { value: "Norway", label: t('common:countries.Norway') },
    { value: "Denmark", label: t('common:countries.Denmark') },
    { value: "Finland", label: t('common:countries.Finland') },
    { value: "Poland", label: t('common:countries.Poland') },
    { value: "Austria", label: t('common:countries.Austria') },
    { value: "Switzerland", label: t('common:countries.Switzerland') },
    { value: "Greece", label: t('common:countries.Greece') },
    { value: "Ireland", label: t('common:countries.Ireland') },
    { value: "Czech Republic", label: t('common:countries.Czech Republic') },
    { value: "Hungary", label: t('common:countries.Hungary') },
    { value: "Romania", label: t('common:countries.Romania') },
    { value: "Bulgaria", label: t('common:countries.Bulgaria') },
    { value: "Croatia", label: t('common:countries.Croatia') },
    { value: "Slovakia", label: t('common:countries.Slovakia') },
    { value: "Slovenia", label: t('common:countries.Slovenia') },
    { value: "Estonia", label: t('common:countries.Estonia') },
    { value: "Latvia", label: t('common:countries.Latvia') },
    { value: "Lithuania", label: t('common:countries.Lithuania') },
    { value: "Luxembourg", label: t('common:countries.Luxembourg') },
    { value: "Malta", label: t('common:countries.Malta') },
    { value: "Cyprus", label: t('common:countries.Cyprus') },
  ];

  const { relationship: initialRelationship, loading } = useSelector(
    (state) => state.relationshipDetails
  );

  const [userRole, setUserRole] = useState(null);
  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded);
  const [relationship, setRelationship] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: "Home", href: "/" },
    { label: "Relationships", href: "/relationships" },
  ]);

  useEffect(() => {
    const locationCaseId = location.state?.caseId;
    const initialCaseFile =
      files?.find((file) => file?._id === locationCaseId) || null;

    if (initialCaseFile) {
      setBreadcrumbs((prevBreadcrumbs) => [
        ...prevBreadcrumbs,
        {
          label: initialCaseFile?.name || "Case File",
          href: `/files/${initialCaseFile._id}/parties`,
        },
      ]);
    }
  }, []);

  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    businessName: "",
    website: "",
    kvk: "",
    bsn: "",
    sex: "",
    burgerlijkeStaat: "",
    type: "",
    status: "",
    dateOfBirth: "",
    contractType: "Individual",
    contacts: [{ name: "", email: "", phone: "", position: "" }],
    addresses: [
      { address: "", postcode: "", place: "", country: "Netherlands" },
    ],

    bankAccounts: [{ accountNumber: "", holder: "", bankAddress: "" }],
  });

  useEffect(() => {
    dispatch(getRelationship(id));
  }, [dispatch, id]);
  useEffect(() => {
    if (user) {
      setUserRole(user.role);
    }
  }, [user]);
  useEffect(() => {
    if (initialRelationship) {
      const formattedRelationship = {
        ...initialRelationship,
        dateOfBirth: initialRelationship?.dateOfBirth
          ? initialRelationship.dateOfBirth.split("T")[0]
          : "",
          contacts: initialRelationship?.contacts || [], // Ensure contacts is an array
          addresses: initialRelationship?.addresses || [], // Ensure addresses is an array
          bankAccounts: initialRelationship?.bankAccounts || [], // Ensure bankAccounts is an array  
      };
      setRelationship(formattedRelationship);
    }
  }, [initialRelationship]);

  const handleSaveChanges = () => {
    dispatch(updateRelationship(id, relationship));
    setTimeout(() => {
      navigate(-1);
    }, 1000);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };
  // General Management
  const handleGeneralChange = (field, value) => {
    setRelationship({ ...relationship, [field]: value });
  };

  const handleFieldChange = (section, index, field, value) => {
    setRelationship((prevState) => {
      const updatedSection = prevState[section]?.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
      return { ...prevState, [section]: updatedSection };
    });
  };

  const handleAddItem = (section, newItem) => {
    setRelationship((prevState) => ({
      ...prevState,
      [section]: [...prevState[section], newItem],
    }));
  };

  const handleRemoveItem = (section, index) => {
    setRelationship((prevState) => ({
      ...prevState,
      [section]: prevState[section].filter((_, i) => i !== index),
    }));
  };

  const sectors = ["Legal", "Corporate", "Finance", "Healthcare", "Technology"];

  const labelStyles = {
    "& .MuiFormControlLabel-label": {
      fontSize: "11.5px",
      fontFamily:
        "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
      fontWeight: 400,
    },
  };

  if (loading || !relationship) {
    return <div>{t('common:loading')}</div>;
  }
  if (Object.keys(relationship).length !== 0) {
    console.log("relationship is ", relationship);
  }
  // return (Object.keys(relationship).length !== 0 &&
  //   userRole !== "Manager" &&
  //   relationship.type === "Lawyer") ||
  //   userRole === "Assistant Lawyer" ? (
  //   <>
  //     <Container
  //       maxWidth="lg"
  //       style={{ position: "relative", paddingBottom: "80px" }}
  //     >
  //       <BreadcrumbNav
  //         title="Edit Relationship"
  //         breadcrumbs={breadcrumbs}
  //         showBreadcrumbs={true}
  //       />
  //       <Box
  //         sx={{
  //           display: "flex",
  //           justifyContent: "center",
  //           alignItems: "center",
  //           height: "60vh",
  //           padding: "30px 24px",
  //           marginTop: "24px",
  //         }}
  //       >
  //         <Typography variant="h6" component="h1">
  //           You are not authorized to view this page.
  //         </Typography>
  //       </Box>
  //     </Container>
  //   </>
  // ) : (
  return (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={t('pages:relationships.edit')}
        breadcrumbs={getBreadcrumbs(t)}
        showBreadcrumbs={true}
      />
      <Box
        sx={{
          backgroundColor: "white",
          padding: "1px 24px 24px 24px ",
          margin: "24px 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            backgroundColor: "white",
            marginTop: "24px",
            border: "1px solid #d1d1d1",
          }}
        >
          <Box sx={{ width: "200px", borderRight: "1px solid #d1d1d1" }}>
            <Box sx={{ marginBottom: "40px", marginTop: "30px" }}>
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                orientation="vertical"
                aria-label="relationship tabs"
                TabIndicatorProps={{
                  sx: {
                    left: 0, // Position the indicator at the start
                  },
                }}
                sx={verticalTabStyles}
              >
                <Tab label={t('common:general')} />
                <Tab label={t('common:contacts')} />
                <Tab label={t('common:addresses')} />
                <Tab label={t('forms:labels.bank_accounts')} />
                {/* <Tab label="Linked Identities" /> */}
              </Tabs>
            </Box>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              paddingLeft: 5,
              paddingTop: 6,
              width: "calc(100% - 200px)",
            }}
          >
            {tabIndex === 0 && (
              <Box
                sx={{ display: "flex", flexWrap: "wrap", marginBottom: "1rem" }}
              >
                {/* Radio Input for Contract Type */}
                <FormControl
                  component="fieldset"
                  sx={{ marginBottom: 2, width: "70%" }}
                >
                  <FormLabel
                    component="legend"
                    sx={{
                      fontSize: "10.5px",
                      fontFamily:
                        "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",

                      fontWeight: 400,
                    }}
                    required
                  >
                    {t('forms:labels.contract_type')}
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-label="contract-type"
                    name="contract-type"
                    value={relationship.contractType}
                    onChange={(e) =>
                      handleGeneralChange("contractType", e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="Individual"
                      control={<Radio />}
                      label={t('forms:labels.individual')}
                      sx={labelStyles}
                    />
                    <FormControlLabel
                      value="Company"
                      control={<Radio />}
                      label={t('forms:labels.company')}
                      sx={labelStyles}
                    />
                  </RadioGroup>
                </FormControl>
                {relationship.contractType === "Individual" ? (
                  <>
                    <TextField
                      label={t('forms:labels.firstName')}
                      value={relationship.firstName}
                      onChange={(e) =>
                        handleGeneralChange("firstName", e.target.value)
                      }
                      required
                      margin="normal"
                      size="small"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.middleName')}
                      value={relationship.middleName}
                      onChange={(e) =>
                        handleGeneralChange("middleName", e.target.value)
                      }
                      margin="normal"
                      size="small"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.lastName')}
                      value={relationship.lastName}
                      onChange={(e) =>
                        handleGeneralChange("lastName", e.target.value)
                      }
                      margin="normal"
                      size="small"
                      required
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                      variant="outlined"
                    />

                    <TextField
                      label={t('forms:labels.bsn')}
                      value={relationship.bsn}
                      onChange={(e) =>
                        handleGeneralChange("bsn", e.target.value)
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                      variant="outlined"
                    />
                    <FormControl
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                      variant="outlined"
                      size="small"
                    >
                      <InputLabel>{t('forms:labels.sex')}</InputLabel>
                      <Select
                        value={relationship?.sex || ""}
                        onChange={(e) =>
                          handleGeneralChange("sex", e.target.value)
                        }
                        label={t('forms:labels.sex')}
                      >
                        <MenuItem value="">
                          <em>{t('forms:options.none')}</em>
                        </MenuItem>
                        {sexOptions?.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                      variant="outlined"
                      size="small"
                    >
                      <InputLabel>{t('forms:labels.burgerlijke_staat')}</InputLabel>
                      <Select
                        value={relationship?.burgerlijkeStaat || ""}
                        onChange={(e) =>
                          handleGeneralChange(
                            "burgerlijkeStaat",
                            e.target.value
                          )
                        }
                        label={t('forms:labels.burgerlijke_staat')}
                      >
                        {maritalStatusOptions?.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {/* <TextField
                      label="Hourly Rate"
                      value={relationship.hourlyRate}
                      margin="normal"
                      onChange={(e) =>
                        handleGeneralChange("hourlyRate", e.target.value)
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                      variant="outlined"
                    /> */}
                  </>
                ) : (
                  <>
                    <TextField
                      label={t('forms:labels.business_name')}
                      value={relationship.businessName}
                      onChange={(e) =>
                        handleGeneralChange("businessName", e.target.value)
                      }
                      required
                      margin="normal"
                      size="small"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.email')}
                      value={relationship.email}
                      onChange={(e) =>
                        handleGeneralChange("email", e.target.value)
                      }
                      size="small"
                      variant="outlined"
                      required
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                    />
                    <TextField
                      label={t('forms:labels.website')}
                      value={relationship.website}
                      onChange={(e) =>
                        handleGeneralChange("website", e.target.value)
                      }
                      margin="normal"
                      size="small"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.kvk')}
                      value={relationship.kvk}
                      onChange={(e) =>
                        handleGeneralChange("kvk", e.target.value)
                      }
                      margin="normal"
                      size="small"
                      sx={textFieldStyles({
                        width: "30%",
                        mt: 3,
                        marginBottom: 18,
                      })}
                      variant="outlined"
                    />
                  </>
                )}

                {/* Type Field */}
                <FormControl
                  sx={textFieldStyles({
                    width: "30%",
                    mt: 3,
                    marginBottom: 18,
                  })}
                  variant="outlined"
                  required
                >
                  <InputLabel id="type-select-filled-label">{t('common:type')}</InputLabel>
                  <Select
                    labelId="type-select-filled-label"
                    value={relationship?.type || ""}
                    onChange={(e) =>
                      handleGeneralChange("type", e.target.value)
                    }
                    size="small"
                    label={t('common:type')}
                  >
                    <MenuItem value="">
                      <em>{t('forms:options.none')}</em>
                    </MenuItem>
                    <MenuItem value="Client">{t('forms:options.relationship_type.client')}</MenuItem>
                    {/* {userRole === "Manager" && ( */}
                    <MenuItem value="Lawyer">{t('forms:options.relationship_type.lawyer')}</MenuItem>
                    {/* )} */}
                    <MenuItem value="Counter Party">{t('forms:options.relationship_type.counter_party')}</MenuItem>
                    <MenuItem value="Others">{t('forms:options.relationship_type.others')}</MenuItem> {/* Add Others option */}
                  </Select>
                </FormControl>
                {/* Status Field */}
                <FormControl
                  sx={textFieldStyles({
                    width: "30%",
                    mt: 3,
                    marginBottom: 18,
                  })}
                  variant="outlined"
                  required
                >
                  <InputLabel id="status-select-filled-label">
                    {t('common:status')}
                  </InputLabel>
                  <Select
                    labelId="status-select-filled-label"
                    value={relationship?.status || ""}
                    onChange={(e) =>
                      handleGeneralChange("status", e.target.value)
                    }
                    size="small"
                    label={t('common:status')}
                  >
                    <MenuItem value="">
                      <em>{t('forms:options.none')}</em>
                    </MenuItem>
                    <MenuItem value="Active">{t('forms:options.status.active')}</MenuItem>
                    <MenuItem value="Inactive">{t('forms:options.status.inactive')}</MenuItem>
                  </Select>
                </FormControl>
                {relationship.contractType === "Individual" ? (
                  <FormControl
                    sx={textFieldStyles({
                      width: "30%",
                      mt: 3,
                      marginBottom: 18,
                    })}
                  >
                    <TextField
                      label={t('forms:labels.date_of_birth')}
                      type="date"
                      value={relationship.dateOfBirth}
                      onChange={(e) =>
                        handleGeneralChange("dateOfBirth", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </FormControl>
                ) : (
                  <></>
                )}
                {/* Sector Field */}
                {/* <FormControl variant="standard" sx={{ mr: 3, mt: 3, width: "30%" }}>
                <InputLabel id="sector-select-filled-label">Sector</InputLabel>
                <Select
                  labelId="sector-select-filled-label"
                  value={relationship.sector}
                  onChange={(e) =>
                    handleGeneralChange("sector", e.target.value)
                  }
                  size="small"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="Finance">Finance</MenuItem>
                  <MenuItem value="Technology">Technology</MenuItem>
                  <MenuItem value="Healthcare">Healthcare</MenuItem>
                </Select>
              </FormControl> */}
              </Box>
            )}

            {tabIndex === 1 && (
              <Box>
                {relationship?.contacts?.map((contact, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 1,
                    }}
                  >
                    <TextField
                      label={t('forms:labels.contact_name')}
                      value={contact.name || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "contacts",
                          index,
                          "name",
                          e.target.value
                        )
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "25%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.email')}
                      value={contact.email || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "contacts",
                          index,
                          "email",
                          e.target.value
                        )
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "20%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.phone')}
                      value={contact.phone}
                      onChange={(e) =>
                        handleFieldChange(
                          "contacts",
                          index,
                          "phone",
                          e.target.value
                        )
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "15%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <FormControl
                      sx={textFieldStyles({
                        width: "15%",
                        mt: 3,
                      })}
                      variant="outlined"
                      size="small"
                    >
                      <InputLabel>{t('forms:labels.position')}</InputLabel>
                      <Select
                        value={contact?.position || ""}
                        onChange={(e) =>
                          handleFieldChange(
                            "contacts",
                            index,
                            "position",
                            e.target.value
                          )
                        }
                        label={t('forms:labels.position')}
                      >
                        <MenuItem value="CEO">{t('forms:options.position.ceo')}</MenuItem>
                        <MenuItem value="CFO">{t('forms:options.position.cfo')}</MenuItem>
                        <MenuItem value="Legal Counsel">{t('forms:options.position.legal_counsel')}</MenuItem>
                        <MenuItem value="HR">{t('forms:options.position.hr')}</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      onClick={() => handleRemoveItem("contacts", index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() =>
                    handleAddItem("contacts", { name: "", email: "" })
                  }
                >
                  {t('buttons:primary.addContact')}
                </Button>
              </Box>
            )}

            {tabIndex === 2 && (
              <Box>
                {relationship?.addresses?.map((address, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 1,
                    }}
                  >
                    <TextField
                      label={t('forms:labels.street')}
                      value={address.address || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "addresses",
                          index,
                          "address",
                          e.target.value
                        )
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "35%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.postcode')}
                      value={address.postcode || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "addresses",
                          index,
                          "postcode",
                          e.target.value
                        )
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "10%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.place')}
                      value={address.place || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "addresses",
                          index,
                          "place",
                          e.target.value
                        )
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "15%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <Autocomplete
                      options={europeanCountries}
                      value={europeanCountries.find(country => country.value === (address.country || "Netherlands")) || europeanCountries[0]}
                      onChange={(e, newValue) =>
                        handleFieldChange("addresses", index, "country", newValue?.value || "Netherlands")
                      }
                      getOptionLabel={(option) => option.label || ""}
                      sx={{ width: "20%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('forms:labels.country')}
                          size="small"
                          sx={textFieldStyles({
                            width: "100%",
                            mt: 3,
                          })}
                          variant="outlined"
                        />
                      )}
                    />
                    <IconButton
                      onClick={() => handleRemoveItem("addresses", index)}
                      sx={{ marginLeft: 2 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() =>
                    handleAddItem("addresses", { street: "", city: "" })
                  }
                >
                  {t('buttons:primary.addAddress')}
                </Button>
              </Box>
            )}

            {tabIndex === 3 && (
              <Box>
                {relationship?.bankAccounts?.map((bank, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 1,
                    }}
                  >
                    <TextField
                      label={t('forms:labels.account_holder_name')}
                      value={bank.holder || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "bankAccounts",
                          index,
                          "holder",
                          e.target.value
                        )
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "25%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.account_number')}
                      value={bank.accountNumber || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "bankAccounts",
                          index,
                          "accountNumber",
                          e.target.value
                        )
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "15%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <TextField
                      label={t('forms:labels.bank_address')}
                      value={bank.bankAddress}
                      onChange={(e) =>
                        handleFieldChange(
                          "bankAccounts",
                          index,
                          "bankAddress",
                          e.target.value
                        )
                      }
                      size="small"
                      sx={textFieldStyles({
                        width: "40%",
                        mt: 3,
                      })}
                      variant="outlined"
                    />
                    <IconButton
                      onClick={() => handleRemoveItem("bankAccounts", index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() =>
                    handleAddItem("bankAccounts", { holder: "", accountNumber: "", bankAddress: "" })
                  }
                >
                  {t('buttons:primary.addBank')}
                </Button>
              </Box>
            )}

            {/* {tabIndex === 4 && (
            <Box>
              {relationship.linkedIdentities.map((identity, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                  <TextField
                    label="Identity Type"
                    value={identity.type || ''}
                    onChange={(e) => handleFieldChange('linkedIdentities', index, 'type', e.target.value)}
                    size="small"
                    sx={{ marginRight: 1, width: '30%' }}
                  />
                  <TextField
                    label="Identifier"
                    value={identity.identifier || ''}
                    onChange={(e) => handleFieldChange('linkedIdentities', index, 'identifier', e.target.value)}
                    size="small"
                    sx={{ marginRight: 1, width: '30%' }}
                  />
                  <IconButton onClick={() => handleRemoveItem('linkedIdentities', index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleAddItem('linkedIdentities', { type: '', identifier: '' })}
              >
                Add Linked Identity
              </Button>
            </Box>
          )} */}
          </Box>
        </Box>

        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            // padding: "10px 16px",
            // background: "#fff",
            zIndex: 10,
            backgroundColor: "white",
            color: "#000000",
            padding: "13px 80px",
          }}
        >
          <CancelButton
            variant="outlined"
            onClick={() => navigate("/relationships")}
            size="small"
          >
            {t('buttons:secondary.cancel')}
          </CancelButton>
          <SubmitButton onClick={handleSaveChanges} size="small">
            {t('buttons:primary.saveChanges')}
          </SubmitButton>
        </Box>
      </Box>
    </Container>
  );
  // );
  // : (
  //   <Typography>Loading...</Typography>
  // );
};

export default ViewEditRelationshipPage;
